import { AgGridReact } from 'ag-grid-react';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { RefObject } from 'react';
import { RIGHT_ALIGNED } from 'common/constants';
import { ComposedTableDataType } from 'components/ComposedTable/types';
import {
  getFooterContent,
  getHeaderContent
} from 'components/ExportOrArchiveDialog/helpers';
import { VendorDataType } from 'pages/VendorsTablePage/components/VendorsTable/types';
import { printParams } from './constants';
import { ColToExportType, ExportParamsType } from './types';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfMake = require('pdfmake/build/pdfmake.js');

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const createHeaderCell = (col: any) => {
  const headerCell: ColToExportType = {
    colId: '',
    style: '',
    text: '',
    alignment: ''
  };
  const headerName = col.colDef?.headerName;
  const headerType = col.colDef?.type;

  headerCell.text = headerName;
  headerCell.colId = col.getColId();
  headerCell.style = 'tableHeader';
  headerCell.alignment = headerType === RIGHT_ALIGNED ? 'right' : '';

  return headerCell;
};

const createTableCell = (
  cellValue: string,
  alignment: string
): { text: string; style: string; alignment: string } => ({
  text: cellValue !== undefined ? cellValue : '',
  style: 'tableCell',
  alignment
});

const getColumnsToExport = <TData extends ComposedTableDataType>(
  gridRef: RefObject<AgGridReact<TData>>
) => {
  const columnsToExport: ColToExportType[] = [
    {
      colId: 'vendorNo',
      style: 'tableHeader',
      text: 'Vendor No.',
      alignment: 'left'
    }
  ];
  gridRef.current?.api.getAllDisplayedColumns().forEach(col => {
    if (col.getColId() !== 'ag-Grid-AutoColumn') {
      const headerCell = createHeaderCell(col);
      columnsToExport.push(headerCell);
    }
  });
  return columnsToExport;
};

const getExportedColumnsWidths = (columnsToExport: ColToExportType[]) =>
  columnsToExport.map(() => `${100 / columnsToExport.length}%`);

export const generatePDFDocument = async <
  TData extends ComposedTableDataType,
  TConvertBlob extends boolean
>(
  gridData: VendorDataType[],
  gridRef: RefObject<AgGridReact<TData>>,
  convertToBlob: TConvertBlob,
  { selectedTab, intl, totalsInFooter, tableHeaderFilters }: ExportParamsType,
  interCompanyFlag?: boolean
): Promise<(TConvertBlob extends true ? Blob : string) | undefined> =>
  new Promise((resolve, reject) => {
    try {
      const {
        PDF_HEADER_HEIGHT,
        PDF_PAGE_ORITENTATION,
        PDF_WITH_HEADER_IMAGE,
        PDF_WITH_FOOTER_PAGE_COUNT,
        PDF_LOGO,
        PDF_INNER_BORDER_COLOR
      } = printParams;
      const columnsToExport = getColumnsToExport(gridRef);
      const widths = getExportedColumnsWidths(columnsToExport);
      const rowsToExport: any = [];
      gridData.forEach(vendorData => {
        const vendorRowData = [
          {
            ...vendorData,
            id: vendorData.vendorNo,
            amount: vendorData.totalAmount,
            discountAmount: vendorData.totalDiscountAmount,
            netAmount: vendorData.totalNetAmount,
            onHold: vendorData.onHold ?? false,
            vendorOnHold: vendorData.onHold ?? false,
            invoiceDescription: '',
            invoiceId: ''
          },
          ...vendorData.invoices
        ];
        vendorRowData.forEach((row: any) => {
          const rowToExport = columnsToExport.map(col => {
            const cellValue = row[col.colId];
            const tableCell = createTableCell(cellValue, col?.alignment ?? '');
            return tableCell;
          });
          rowsToExport.push(rowToExport);
        });
      });
      const headerRows = 1;
      const pageMargins = [
        50,
        PDF_WITH_HEADER_IMAGE ? 70 : 20,
        50,
        PDF_WITH_FOOTER_PAGE_COUNT ? 40 : 10
      ];
      const headerContent = getHeaderContent(
        selectedTab,
        intl,
        tableHeaderFilters,
        undefined,
        interCompanyFlag
      );
      const footerContent = getFooterContent(selectedTab, intl, totalsInFooter);
      const pdfGen = pdfMake.createPdf({
        pageOrientation: PDF_PAGE_ORITENTATION,
        content: [
          {
            text: headerContent
          },
          {
            style: 'myTable',
            table: {
              headerRows,
              widths,
              body: [columnsToExport, ...rowsToExport]
            },
            layout: {
              hLineWidth: () => 1,
              vLineWidth: () => 1,
              hLineColor: () => PDF_INNER_BORDER_COLOR,
              vLineColor: () => PDF_INNER_BORDER_COLOR
            }
          },
          {
            text: footerContent,
            style: 'footer'
          }
        ],
        images: {
          'ag-grid-logo': PDF_LOGO
        },
        styles: {
          myTable: {
            margin: [0, 40, 0, 0],
            fontSize: 8
          },
          tableHeader: {
            bold: true,
            margin: [0, PDF_HEADER_HEIGHT / 3, 0, 0]
          },
          tableCell: {},
          footer: {
            alignment: 'right',
            margin: [0, 20, 0, 0],
            bold: true
          }
        },
        pageMargins
      });
      if (convertToBlob) {
        pdfGen.getBlob(resolve);
      } else {
        pdfGen.getBase64(resolve);
      }
    } catch (e) {
      console.log(`error in generating pdf: ${e}`);
      reject(e);
    }
  });
